import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { toProperCase, PROPER_CASE_FIELDS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Building2, Pencil, Globe, IndianRupee, Eye, Merge } from 'lucide-react';
import { PaginationControls } from '../components/PaginationControls';
import { useClientPagination } from '../hooks/useClientPagination';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/bulk/BulkActionBar';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { SimilarCustomerWarning } from '../components/customers/SimilarCustomerWarning';

const CUSTOMER_CSV_COLUMNS = [
  { key: 'customer_code', label: 'Code' }, { key: 'name', label: 'Name' },
  { key: 'customer_type', label: 'Type' }, { key: 'country', label: 'Country' },
  { key: 'contact_person', label: 'Contact' }, { key: 'contact_email', label: 'Email' },
  { key: 'gst_number', label: 'GST' }, { key: 'currency', label: 'Currency' },
];

const COUNTRIES = ['India', 'USA', 'UK', 'Germany', 'Japan', 'China', 'UAE', 'Singapore', 'Other'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'AED', 'SGD'];

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const pagination = useClientPagination(customers);
  const selection = useBulkSelection(pagination.pageRows);
  const { user } = useAuth();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { is_active: true };
      if (search) params.search = search;
      if (typeFilter) params.customer_type = typeFilter;
      const response = await customersAPI.list(params);
      setCustomers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleCreateOrUpdate = async (data) => {
    try {
      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, data);
        toast.success('Customer updated');
      } else {
        const res = await customersAPI.create(data);
        toast.success(`Customer created: ${res.data.customer_code}`);
      }
      setDialogOpen(false);
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (errorDetail?.msg || errorDetail?.message || 'Operation failed');
      toast.error(errorMessage);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">Customers</h1>
          <p className="text-muted-foreground">Manage your customer database</p>
        </div>
        <div className="flex gap-2">
        {user?.role === 'admin' && (
          <Button variant="outline" onClick={() => navigate('/customers/duplicates')}
            data-testid="find-duplicates-btn">
            <Merge className="mr-2 h-4 w-4" />Find duplicates
          </Button>
        )}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingCustomer(null);
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-customer-btn">
              <Plus className="mr-2 h-4 w-4" />Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </DialogTitle>
            </DialogHeader>
            <CustomerForm
              customer={editingCustomer}
              onSubmit={handleCreateOrUpdate}
              onCancel={() => { setDialogOpen(false); setEditingCustomer(null); }}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="customer-search-input"
              />
            </div>
            <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="domestic">Domestic</SelectItem>
                <SelectItem value="export">Export</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActionBar
            entity="customers"
            selectedIds={selection.selectedIds}
            selectedRows={selection.selectedRows}
            onClear={selection.clear}
            onDone={fetchCustomers}
            csvColumns={CUSTOMER_CSV_COLUMNS}
            csvPrefix="customers"
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No customers found</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.allOnPageSelected}
                        onCheckedChange={selection.togglePage}
                        aria-label="Select all customers on this page"
                        data-testid="customers-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>GST/PAN</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagination.pageRows.map((c) => (
                    <TableRow key={c.id} className="table-row"
                      data-state={selection.isSelected(c.id) ? 'selected' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selection.isSelected(c.id)}
                          onCheckedChange={() => selection.toggleOne(c)}
                          aria-label={`Select ${c.name}`}
                          data-testid={`customers-row-checkbox-${c.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-medium">{c.customer_code || c.code}</TableCell>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <Badge className={c.customer_type === 'export' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {c.customer_type === 'export' ? <><Globe className="h-3 w-3 mr-1" />Export</> : 'Domestic'}
                        </Badge>
                      </TableCell>
                      <TableCell>{c.country || 'India'}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{c.contact_person || '-'}</p>
                          <p className="text-muted-foreground text-xs">{c.contact_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {c.gst_number && <div>GST: {c.gst_number}</div>}
                        {c.pan_number && <div>PAN: {c.pan_number}</div>}
                        {!c.gst_number && !c.pan_number && '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{c.currency || 'INR'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/customers/${c.id}`)}
                            data-testid={`view-customer-${c.id}`}
                            title="View 360° history"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingCustomer(c); setDialogOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <PaginationControls
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onLimitChange={pagination.setLimit}
            onPageChange={pagination.setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function CustomerForm({ customer, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    customer_type: customer?.customer_type || 'domestic',
    country: customer?.country || 'India',
    currency: customer?.currency || 'INR',
    address: customer?.address || '',
    city: customer?.city || '',
    state: customer?.state || '',
    pincode: customer?.pincode || '',
    gst_number: customer?.gst_number || '',
    pan_number: customer?.pan_number || '',
    contact_person: customer?.contact_person || '',
    contact_email: customer?.contact_email || '',
    contact_phone: customer?.contact_phone || '',
    payment_terms: customer?.payment_terms || '',
    credit_days: customer?.credit_days || 30,
    default_terms: customer?.default_terms || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlurProperCase = (e) => {
    const { name, value } = e.target;
    if (PROPER_CASE_FIELDS.has(name) && value) {
      setFormData((prev) => ({ ...prev, [name]: toProperCase(value) }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Customer Code - Auto-generated (shown only in edit mode) */}
      {customer && (
        <div className="p-3 bg-muted/50 rounded-lg">
          <Label className="text-muted-foreground text-xs">Customer Code (Auto-generated)</Label>
          <p className="font-mono font-bold">{customer.customer_code || customer.code}</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="name">Company Name *</Label>
        <Input id="name" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlurProperCase} required data-testid="customer-name-input" />
        <SimilarCustomerWarning name={formData.name} gstNumber={formData.gst_number} excludeId={customer?.id} />
      </div>

      {/* NEW: Customer Type & Country */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Customer Type *</Label>
          <Select value={formData.customer_type} onValueChange={(v) => setFormData(p => ({ ...p, customer_type: v }))}>
            <SelectTrigger data-testid="customer-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="domestic">Domestic</SelectItem>
              <SelectItem value="export">Export</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Country *</Label>
          <Select value={formData.country} onValueChange={(v) => setFormData(p => ({ ...p, country: v }))}>
            <SelectTrigger data-testid="customer-country-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select value={formData.currency} onValueChange={(v) => setFormData(p => ({ ...p, currency: v }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <Input id="address" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlurProperCase} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input id="city" name="city" value={formData.city} onChange={handleChange} onBlur={handleBlurProperCase} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Input id="state" name="state" value={formData.state} onChange={handleChange} onBlur={handleBlurProperCase} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pincode">Pincode</Label>
          <Input id="pincode" name="pincode" value={formData.pincode} onChange={handleChange} />
        </div>
      </div>

      {/* NEW: GST & PAN */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="gst_number">GST Number</Label>
          <Input id="gst_number" name="gst_number" value={formData.gst_number} onChange={handleChange} placeholder="22AAAAA0000A1Z5" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pan_number">PAN Number</Label>
          <Input id="pan_number" name="pan_number" value={formData.pan_number} onChange={handleChange} placeholder="AAAAA0000A" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contact_person">Contact Person</Label>
          <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleChange} onBlur={handleBlurProperCase} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_email">Email</Label>
          <Input id="contact_email" name="contact_email" type="email" value={formData.contact_email} onChange={handleChange} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_phone">Phone</Label>
          <Input id="contact_phone" name="contact_phone" value={formData.contact_phone} onChange={handleChange} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="payment_terms">Payment Terms</Label>
          <Input id="payment_terms" name="payment_terms" value={formData.payment_terms} onChange={handleChange} onBlur={handleBlurProperCase} placeholder="Net 30" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="credit_days">Credit Days</Label>
          <Input id="credit_days" name="credit_days" type="number" value={formData.credit_days} onChange={handleChange} />
        </div>
      </div>

      {/* Default Terms & Conditions */}
      <div className="space-y-2">
        <Label htmlFor="default_terms">Default Terms & Conditions</Label>
        <textarea
          id="default_terms"
          name="default_terms"
          value={formData.default_terms}
          onChange={handleChange}
          placeholder="Enter default terms that will auto-fill when creating quotations for this customer (e.g., Payment: 100% Advance)"
          className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          rows={4}
        />
        <p className="text-xs text-muted-foreground">These terms will automatically populate in new quotations for this customer.</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="btn-primary" data-testid="save-customer-btn">
          {customer ? 'Update' : 'Create'} Customer
        </Button>
      </div>
    </form>
  );
}
